import AffiliateSupport from "./Auth/Affiliate/AffiliateSupport";
  
import AffiliateAssets from "./Auth/Affiliate/AffiliateAssets";
import AdminActionLog from "./Pages/Admin/AdminActionLog";
import UserManagement from "./Pages/Admin/UserManagement";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { useUser } from "./context/UserContext";
import Navbar from "./components/User/Navbar";
import Footer from "./components/User/Footer";
import ScrollToTop from "./components/ScrollToTop";
import AdminGate from "./components/AdminGate";

// User auth
import Login from "./Auth/User/Login";
import Signup from "./Auth/User/SignUp";
import AuthChoice from "./Auth/User/AuthChoice";
import VerificationPage from "./Auth/User/VerificationPage";
import ForgotPassword from "./Auth/User/ForgotPassword";
import ResetPassword from "./Auth/User/ResetPassword";

// Admin auth
import LoginAdmin from "./Auth/Admin/LoginAdmin";
import PaymentCallback from './components/PaymentCallback';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentFailed from './components/PaymentFailed';
import DiningPaymentCallback from './components/DiningPaymentCallback';
import DiningPaymentSuccess from './components/DiningPaymentSuccess';
// Affiliate pages
import AffiliateRegister from "./Auth/Affiliate/AffiliateRegister";
import AffiliateLogin from "./Auth/Affiliate/AffiliateLogin";
import AffiliateDashboard from "./Auth/Affiliate/AffiliateDashboard";
// User pages
import HomePage from "./Pages/User/HomePage";
import Residencies from "./Pages/User/Residencies";
import About from "./Pages/User/About";
import Contact from "./Pages/User/Contact";
import FoodMenu from "./Pages/User/Menu";
import OneBedroomPage from "./Pages/User/Rooms/OneBedroomPage";
import TwoBedroomPage from "./Pages/User/Rooms/2bedroomPage";
import PhotoGallery from "./Pages/User/PhotoGallery";
import ProfilePage from "./Pages/User/ProfilePage";
import ReservationDetails from "./Pages/User/ReservationDetail";
import HeroSection from "./Pages/User/HeroSection";
import FAQ from "./components/User/FAQ";
import FaqBlack from "./components/User/FAQBlack";

// Privacy and Legal pages
import PrivacyPolicy from "./Pages/Legal/PrivacyPolicy";
import CCPACompliance from "./Pages/Legal/CCPACompliance";
import TermsOfService from "./Pages/Legal/TermsOfService";

// Privacy components
import CookieConsent from "./components/Privacy/CookieConsent";
import { PrivacyProvider } from "./context/PrivacyContext";

//Admin Pges
import BookingManagement from "./Pages/Admin/BookingManagement";
import BookingDetails from "./Pages/Admin/BookingDetails";
import RoomManagement from "./Pages/Admin/RoomManagement";
import RoomDetails from "./Pages/Admin/RoomDetails";
import RoomTypeManagement from "./Pages/Admin/RoomTypeManagement";
import UserProfile from "./Pages/Admin/UserProfile";
import Payments from "./Pages/Admin/Payments";
import OrderManagement from "./Pages/Admin/OrderManagement";
import KitchenDashboard from "./Pages/Kitchen/KitchenDashboard";
import Settings from "./Pages/Admin/Settings";
import AdminPoliciesPage from "./Pages/Admin/AdminPoliciesPage";
import Affiliate from "./Pages/Admin/Affiliate";
import AdminGallery from "./Pages/Admin/AdminGallery";
import Promotions from "./Pages/Admin/Promotions";
import AdminReviews from "./Pages/Admin/AdminReviews";
import OrderPage from "./Pages/User/OrderPage";

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useUser();
  const timerRef = useRef(null);
  
  // Inactivity timeout in milliseconds (15 minutes)
  const TIMEOUT = 15 * 60 * 1000;

  // Reset inactivity timer
  const resetTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      // Auto-logout: clear user and redirect
      setUser(null);
      localStorage.removeItem("user"); 
      localStorage.removeItem("token");
      navigate("/login", { replace: true });
    }, TIMEOUT);
  };

  useEffect(() => {
    // Only set up timer if user is logged in
    if (!user) return;

    // List of events that indicate activity
    const events = ["mousemove", "keydown", "mousedown", "touchstart"];
    events.forEach(event => window.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      events.forEach(event => window.removeEventListener(event, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    // Check user state from localStorage on initial load
    const storedUserRaw = localStorage.getItem("user");
    if (storedUserRaw) {
      try {
        const storedUser = JSON.parse(storedUserRaw);
        setUser(storedUser);
        if (storedUser.role === "super_admin" && !location.pathname.startsWith("/admin")) {
          navigate("/admin/BookingManagement", { replace: true });
        }
      } catch (err) {
        // If parsing fails, remove corrupted value and don't redirect — let router decide
        console.warn("Failed to parse stored user:", err);
        localStorage.removeItem("user");
      }
    }
    // Do not force navigation when no user is found; let the router render the current URL
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect if current path starts with "/admin"
  const isAdminPage = location.pathname.startsWith("/admin");
  const isKitchenPage = location.pathname.startsWith("/kitchen");
  const isDiningPage = location.pathname === "/dining";

  return (
    <div className="app-container">
      <ScrollToTop />
      {!isAdminPage && !isKitchenPage && !isDiningPage && <Navbar />}
      
      <CookieConsent />

  <Routes>
    {/* Affiliate routes */}
    <Route path="/affiliate/login" element={<AffiliateLogin />} />
    <Route path="/affiliate/register" element={<AffiliateRegister />} />
    <Route path="/AffiliateDashboard" element={<AffiliateDashboard />} />
    <Route path="/affiliate/assets" element={<AffiliateAssets />} />
        <Route path="/affiliate/support" element={<AffiliateSupport />} />
        {/* Admin-only pages */}
        <Route path="/admin/loginAdmin" element={<LoginAdmin />} />
        <Route element={<AdminGate />}>
          <Route path="/admin/UserManagement" element={<UserManagement />} />
          <Route
            path="/admin/BookingManagement"
            element={<BookingManagement />}
          />
          <Route path="/admin/bookings/details/:id" element={<BookingDetails />} />

          <Route path="/admin/RoomManagement" element={<RoomManagement />} />
          <Route path="/admin/RoomTypeManagement" element={<RoomTypeManagement />} />
          <Route path="/admin/room-details/:id" element={<RoomDetails />} />

          <Route path="/admin/UserProfile" element={<UserProfile />} />
          <Route path="/admin/policies" element={<AdminPoliciesPage />} />
          <Route path="/admin/Payments" element={<Payments />} />
          <Route path="/admin/orders" element={<OrderManagement />} />
          <Route path="/admin/Settings" element={<Settings />} />
          <Route path="/admin/affiliate" element={<Affiliate />} />
          <Route path="/admin/action-log" element={<AdminActionLog />} />
          <Route path="/admin/gallery" element={<AdminGallery />} />
          <Route path="/admin/promotions" element={<Promotions />} />
          <Route path="/admin/reviews" element={<AdminReviews />} />
        </Route>

        {/* Kitchen Routes */}
        <Route path="/kitchen/dashboard" element={<KitchenDashboard />} />

        {/* Public-facing user pages */}
        <Route
          path="/"
          element={
            <>
              <HeroSection />
              <main className="main-content">
                <HomePage />
                <FAQ />
              </main>
            </>
          }
        />
        <Route
          path="/residencies"
          element={
            <main className="main-content">
              <Residencies />
            </main>
          }
        />
        <Route path="/dining" element={<OrderPage />} />
        <Route
          path="/menu"
          element={
            <main className="main-content-noPad">
              <FoodMenu />
            </main>
          }
        />
        <Route
          path="/about"
          element={
            <main className="main-content-blue">
              <About />
            </main>
          }
        />
        <Route
          path="/contact"
          element={
            <main className="main-content">
              <Contact />
              <FAQ />
            </main>
          }
        />
        <Route
          path="/gallery"
          element={
            <main className="main-content-noPad">
              <PhotoGallery />
            </main>
          }
        />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/rooms/:id" element={<OneBedroomPage />} />

        <Route path="/payment/callback" element={<PaymentCallback />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/payment-failed" element={<PaymentFailed />} />
        <Route path="/dining/payment/callback" element={<DiningPaymentCallback />} />
        <Route path="/dining/payment-success" element={<DiningPaymentSuccess />} />

        <Route
          path="/2BedroomPage"
          element={
            <main className="main-content-noPad">
              <TwoBedroomPage />
            </main>
          }
        />
        <Route
          path="/reservation-details/:id" // Remove the $ symbol
          element={
            <main className="main-content">
              <ReservationDetails />
            </main>
          }
        />

        {/* Privacy and Legal Routes */}
        <Route
          path="/privacy-policy"
          element={
            <main className="main-content">
              <PrivacyPolicy />
            </main>
          }
        />
        <Route
          path="/privacy-rights"
          element={
            <main className="main-content">
              <CCPACompliance />
            </main>
          }
        />
        <Route
          path="/terms-of-service"
          element={
            <main className="main-content">
              <TermsOfService />
            </main>
          }
        />
      </Routes>

      {!isAdminPage && !isKitchenPage && <Footer />}
    </div>
  );
}

export default AppContent;
